/**
 * A panel is meant as a floating container of components. The component provides consistent styling for the border area
 * around the nested component
 */

import React, { CSSProperties, ReactNode, Ref } from "react";
import { observer } from "mobx-react";
import classNames from "classnames";

export enum PanelTheme {
  Light = "light",
  Dark = "dark",
}

export interface IPanelProps {
  /**
   * The theme of the Panel.
   */
  theme?: PanelTheme;
  /**
   * We provide access to applying class names to the component so that spacing, and similar mixins, can
   * be applied conveniently. Other styling should be done through theming.
   */
  className?: string;
  style?: CSSProperties;
  children?: ReactNode;
  id?: string;
  role?: string;
}

interface IPanelInnerProps extends IPanelProps {
  /**
   * Optional ref to the panel div node forwarded through React.forwardRef.
   */
  innerRef?: Ref<HTMLDivElement>;
}

@observer
class Panel extends React.Component<IPanelInnerProps> {
  public static defaultProps = {
    theme: PanelTheme.Light,
    className: "",
  };

  public render() {
    const { theme, className, style, innerRef, id, role } = this.props;
    const classes = classNames(["panel", `panel--${theme}`, className]);
    return (
      <div className={classes} style={style} ref={innerRef} id={id} role={role}>
        {this.props.children}
      </div>
    );
  }
}

export default React.forwardRef<HTMLDivElement, IPanelProps>((props: IPanelProps, ref: Ref<HTMLDivElement>) => (
  <Panel {...props} innerRef={ref} />
));
